import apiClient from './client';

export interface Product {
  id: string;
  name: string;
  brand?: string | { id: string; name: string; _id?: string };
  model?: string;
  category: string | { id: string; name: string; _id?: string };
  branch: string | { id: string; name: string; _id?: string };
  createdBy: string;
  isActive: boolean;
  deviceCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductRequest {
  name: string;
  brand?: string;
  model?: string;
  category: string;
}

export interface UpdateProductRequest {
  name?: string;
  brand?: string;
  model?: string;
  category?: string;
  isActive?: boolean;
}

export interface StockDetailDevice {
  id: string;
  serialNumber: string;
  status: string;
  condition: string;
  location: string;
  locationType: string;
}

export interface StockDetailBranch {
  branchId: string;
  branchName: string;
  total: number;
  inStock: number;
  assigned: number;
  inRepair: number;
  devices: StockDetailDevice[];
}

export interface StockDetailResponse {
  product: {
    id: string;
    name: string;
    brand?: string;
    model?: string;
  };
  totalDevices: number;
  branches: StockDetailBranch[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const productsApi = {
  getAll: async (): Promise<Product[]> => {
    const response = await apiClient.get('/products');
    return response.data.map((product: any) => ({
      ...product,
      id: product._id,
      deviceCount: product.deviceCount || 0,
    }));
  },

  getAllPaginated: async (page: number = 1, limit: number = 50, category?: string, brand?: string, branch?: string, search?: string): Promise<PaginatedResponse<Product>> => {
    const params: any = { page, limit };
    if (category && category !== 'all') {
      params.category = category;
    }
    if (brand && brand !== 'all') {
      params.brand = brand;
    }
    if (branch && branch !== 'all') {
      params.branch = branch;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const response = await apiClient.get('/products', { params });
    return {
      ...response.data,
      data: response.data.data.map((product: any) => ({
        ...product,
        id: product._id,
        deviceCount: product.deviceCount || 0,
        // Convert nested _id to id for populated fields
        category: product.category?._id ? {
          ...product.category,
          id: product.category._id
        } : product.category,
        brand: product.brand?._id ? {
          ...product.brand,
          id: product.brand._id
        } : product.brand,
        branch: product.branch?._id ? {
          ...product.branch,
          id: product.branch._id
        } : product.branch,
      })),
    };
  },

  search: async (query: string): Promise<Product[]> => {
    const response = await apiClient.get('/products/search', { params: { q: query } });
    return response.data.map((product: any) => ({
      ...product,
      id: product._id,
      deviceCount: product.deviceCount || 0,
    }));
  },

  getProblematic: async (): Promise<any> => {
    const response = await apiClient.get('/products/problematic');
    return response.data;
  },

  getById: async (id: string): Promise<Product> => {
    const response = await apiClient.get(`/products/${id}`);
    return {
      ...response.data,
      id: response.data._id,
    };
  },

  create: async (data: CreateProductRequest): Promise<Product> => {
    const response = await apiClient.post('/products', data);
    return {
      ...response.data,
      id: response.data._id,
    };
  },

  update: async (id: string, data: UpdateProductRequest): Promise<Product> => {
    const response = await apiClient.put(`/products/${id}`, data);
    return {
      ...response.data,
      id: response.data._id,
    };
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/products/${id}`);
  },

  getStockDetail: async (id: string, branchId?: string): Promise<StockDetailResponse> => {
    const params = branchId ? { branchId } : {};
    const response = await apiClient.get(`/products/${id}/stock-detail`, { params });
    return response.data;
  },

  mergeProducts: async (sourceProductId: string, targetProductId: string): Promise<any> => {
    const response = await apiClient.post(`/products/${sourceProductId}/merge`, {
      targetProductId,
    });
    return response.data;
  },
};
